import './TimeFrameSelector.scss';
import React, { useState, useMemo, useEffect } from 'react';
import { coerceEpoch, epochToDateTimeString } from 'helpers/common.helper';
import { DATE_FORMAT_OPTIONS } from 'pages/AnalyticsV2/Overview/constants';
import { FlightSelect } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';
import { AdvancedDateRange, ChartConfigState, INTERVAL_OPTIONS } from 'pages/AnalyticsV2/types';
import { TIMEFRAME_INTERVAL_OPTIONS } from 'pages/AnalyticsV2/CustomAnalytics/CreateOrEditChart/constants';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];

const sameDateRef = (a: Date | null | undefined, b: Date | null | undefined) =>
  a === b || (!!a && !!b && a.getTime() === b.getTime());

const isSameRange = (a: AdvancedDateRange, b: AdvancedDateRange) =>
  sameDateRef(a[0] as Date | null, b[0] as Date | null) &&
  sameDateRef(a[1] as Date | null, b[1] as Date | null) &&
  a[2] === b[2] &&
  a[3] === b[3];

const MAIN_CLASS = 'timeframe-selector';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  BLOCK: `${MAIN_CLASS}__block`,
  PREVIEW: `${MAIN_CLASS}__preview`,
  DATE_RANGE: `${MAIN_CLASS}__date-range`,
  DATE_RANGE_DD: `${MAIN_CLASS}__date-range__dropdown`,
};

interface TimeFrameSelectorProps {
  chartConfig: ChartConfigState;
  onChange: (timeFrame: ChartConfigState['timeFrame']) => void;
  onValidation: (isValid: boolean) => void;
  hiddenIntervals?: INTERVAL_OPTIONS[];
}

export default function TimeFrameSelector({
  chartConfig,
  onChange,
  onValidation,
  hiddenIntervals,
}: TimeFrameSelectorProps) {
  const { start, end, period, relative } = chartConfig.timeFrame || {};

  // Reconstruct the picker tuple from the parent's current chartConfig.timeFrame.
  // Used to seed `selectedDateRange` on mount AND to re-sync when the parent loads
  // an existing chart asynchronously (chartConfig defaults are replaced after the
  // module config resolves, so the picker must reflect the loaded values).
  const dateRangeFromProps = useMemo<AdvancedDateRange>(() => {
    if (start && end) {
      return [start, end];
    } else if (relative && relative[0] !== undefined && relative[0] >= 0 && !!relative[1]) {
      // right now 'Today' is serialized with relatvieTimeOffset as 1 ... but the design system component expect 0
      return [null, null, relative[0] === 1 ? 0 : relative[0], relative[1]];
    }
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    return [startOfToday, endOfToday, 7, INTERVAL_OPTIONS.DAILY];
  }, [start, end, relative]);

  const [selectedDateRange, setSelectedDateRange] = useState<AdvancedDateRange>(dateRangeFromProps);

  // TODO: revisit the need for this effect — if the parent is properly seeding the picker's initial value on mount, and the picker is properly reflecting changes to the parent's chartConfig.timeFrame (which should be the source of truth), then this syncing logic should be unnecessary. The picker's own internal state should be the single source of truth for the currently selected range, and the parent should only update its chartConfig.timeFrame when the user makes a selection in the picker (handled in handleDateRangeChange) — in which case there should never be a scenario where the parent has a meaningfully different time frame than the picker's current selection, because they should always be in sync. If we do find ourselves needing to re-sync from the parent like this, it may be a sign that there's a hole in that logic somewhere — e.g. maybe the parent is updating its chartConfig.timeFrame from some other source besides the picker, and those updates aren't being reflected back down to the picker for some reason.
  // Re-seed when the parent props change to a meaningfully different range — covers
  // the edit-chart flow where chartConfig is hydrated asynchronously after mount.
  // No-ops when the parent echo matches the user's own pick (same tuple shape).
  useEffect(() => {
    setSelectedDateRange((current) => (isSameRange(current, dateRangeFromProps) ? current : dateRangeFromProps));
  }, [dateRangeFromProps]);

  const intervalOptions = useMemo(
    () =>
      TIMEFRAME_INTERVAL_OPTIONS.map((interval) => {
        const { label, value, duration } = interval;
        return { key: value, name: label, duration };
      }).filter((opt) => {
        if (opt.key && hiddenIntervals?.includes(opt.key as INTERVAL_OPTIONS)) {
          return false;
        }
        const dateRange = selectedDateRange;
        const [start, end, relativeTimeOffset, relativeTimeUnit] = dateRange;

        if (relativeTimeOffset === 0) {
          return opt.key === INTERVAL_OPTIONS.HOURLY;
        } else if (start && end) {
          const duration = coerceEpoch(end) - coerceEpoch(start);
          return duration > opt.duration;
        } else if (relativeTimeOffset && relativeTimeUnit) {
          const intervalOption =
            TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === relativeTimeUnit) || TIMEFRAME_INTERVAL_OPTIONS[0];
          return relativeTimeOffset * intervalOption.duration > opt.duration;
        }
      }),
    [selectedDateRange, hiddenIntervals],
  );

  const initialTimeInterval = useMemo(
    () =>
      period
        ? {
            key: period as INTERVAL_OPTIONS,
            name: TIMEFRAME_INTERVAL_OPTIONS.find((intervalOption) => intervalOption.value === period)?.label || '',
            duration: coerceEpoch(selectedDateRange?.[1] || 0) - coerceEpoch(selectedDateRange?.[0] || 0),
          }
        : intervalOptions[1],
    [period, selectedDateRange, intervalOptions],
  );

  const [selectedTimeInterval, setSelectedTimeInterval] = useState<(typeof intervalOptions)[number] | undefined>();

  const handleDateRangeChange = (selectedDate: AdvancedDateRange) => {
    setSelectedDateRange(selectedDate);

    const [start, end, relativeTimeOffset, relativeTimeUnit] = selectedDate;

    // `selectedTimeInterval || initialTimeInterval` can be undefined when the picked
    // range has zero duration (first-pick-of-custom echoes `[day, day]`) because
    // every interval gets filtered out — destructuring would throw and abort the
    // picker's close sequence. Default to an empty shape instead.
    const { key, duration } = selectedTimeInterval || initialTimeInterval || { key: undefined, duration: 0 };

    let isValidInterval = true;
    if (start && end) {
      const newDuration = coerceEpoch(end) - coerceEpoch(start);

      isValidInterval = newDuration > (duration || 0);
      !isValidInterval && setSelectedTimeInterval(undefined);
    }

    onChange({
      start,
      end,
      period: isValidInterval ? key : undefined,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  };

  const handleIntervalChange = ({ key, name, duration }: { key: INTERVAL_OPTIONS; name: string; duration: number }) => {
    setSelectedTimeInterval({
      key,
      name,
      duration,
    });

    const [start, end, relativeTimeOffset, relativeTimeUnit] = selectedDateRange;

    onChange({
      start,
      end,
      period: key,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  };

  const showPreview = () => {
    const dateRange = selectedDateRange;
    const [start, end, relativeTimeOffset] = dateRange;

    const shouldShowPreview = start && end && relativeTimeOffset !== null && relativeTimeOffset !== undefined;

    return shouldShowPreview ? (
      <div className={CLASSES.PREVIEW}>
        {relativeTimeOffset === 0
          ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
          : `${epochToDateTimeString(start, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
              end,
              'en-US',
              DATE_FORMAT_OPTIONS,
            )}`}
      </div>
    ) : null;
  };

  useEffect(() => {
    const dateRange = selectedDateRange;
    const timeInterval = selectedTimeInterval || initialTimeInterval;
    const isTimePeriodValid =
      relative?.[0] !== undefined && relative?.[0] !== null && relative?.[1]
        ? dateRange[2] !== undefined && dateRange[2] !== null && !!dateRange[3]
        : !!dateRange[0] && !!dateRange[1];
    const isTimeIntervalValid = !!timeInterval?.key;

    const isValid = isTimePeriodValid && isTimeIntervalValid;
    onValidation(isValid);
  }, [selectedDateRange, selectedTimeInterval, initialTimeInterval, relative, onValidation]);

  return (
    <section className={MAIN_CLASS}>
      <label className={CLASSES.LABEL} htmlFor="select-period">
        Select a period of time
      </label>
      <div className={CLASSES.BLOCK}>
        <DateRangePicker
          className={`${CLASSES.DATE_RANGE} half`}
          value={selectedDateRange}
          onChange={handleDateRangeChange}
          maxDate={new Date()}
          presets={DATERANGE_PRESETS}
          ariaLabel="Select a time frame"
          id="select-period"
        />
        {showPreview()}
      </div>
      <label className={CLASSES.LABEL} htmlFor="select-interval">
        Select an interval
      </label>
      <FlightSelect
        className={`${CLASSES.DATE_RANGE_DD}`}
        options={intervalOptions}
        selected={selectedTimeInterval || initialTimeInterval}
        ariaControlsId={'select-interval'}
        width={'100%'}
        handleOptionClick={handleIntervalChange}
      />
    </section>
  );
}
